import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollText, RefreshCw, Filter, Download, Archive } from "lucide-react";
import AuditStatsCards from "@/components/AuditStatsCards";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const csvEscape = (val) => {
  if (val === null || val === undefined) return "";
  const s = typeof val === "string" ? val : JSON.stringify(val);
  if (/[",\n;]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const ACTION_COLORS = {
  "auth.login.success": "text-green-400",
  "auth.login.failed": "text-red-400",
  "auth.login.2fa_required": "text-amber-400",
  "auth.2fa.failed": "text-red-400",
  "auth.logout": "text-[#8899AA]",
  "2fa.enabled": "text-green-400",
  "2fa.disabled": "text-amber-400",
  "user.created": "text-green-400",
  "user.updated": "text-amber-400",
  "user.deleted": "text-red-400",
  "event.created": "text-green-400",
  "event.updated": "text-amber-400",
  "event.deleted": "text-red-400",
  "event.series.created": "text-green-400",
  "job.created": "text-green-400",
  "job.updated": "text-amber-400",
  "job.deleted": "text-red-400",
  "newsletter.campaign.sent": "text-blue-400",
  "charts.config.updated": "text-amber-400",
};

const fmtDate = (d) => {
  try {
    return new Date(d.replace(" ", "T") + "Z").toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return d; }
};

export default function AdminAuditTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [retention, setRetention] = useState(180);
  const [retentionInput, setRetentionInput] = useState("");
  const [savingRetention, setSavingRetention] = useState(false);

  const fetchRetention = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/audit-retention`, { withCredentials: true });
      setRetention(data.days);
      setRetentionInput(String(data.days));
    } catch (err) {
      console.error(err);
    }
  }, []);

  const saveRetention = async () => {
    const days = parseInt(retentionInput, 10);
    if (!days || days < 7 || days > 3650) return;
    setSavingRetention(true);
    try {
      const { data } = await axios.put(`${API}/settings/audit-retention`, { days }, { withCredentials: true });
      setRetention(data.days);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingRetention(false);
    }
  };

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/audit?limit=300`, { withCredentials: true });
      setEntries(data);
    } catch (err) {
      console.error("Audit fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLog(); fetchRetention(); }, [fetchLog, fetchRetention]);

  const uniqueActions = useMemo(() => {
    const set = new Set();
    entries.forEach((e) => { if (e.action) set.add(e.action); });
    return Array.from(set).sort();
  }, [entries]);

  const filtered = entries.filter((e) => {
    if (actionFilter && e.action !== actionFilter) return false;
    if (!filter) return true;
    const s = filter.toLowerCase();
    return (
      (e.action || "").toLowerCase().includes(s) ||
      (e.user_email || "").toLowerCase().includes(s) ||
      (e.resource_type || "").toLowerCase().includes(s) ||
      (e.resource_id || "").toLowerCase().includes(s) ||
      JSON.stringify(e.details || {}).toLowerCase().includes(s)
    );
  });

  const exportCsv = () => {
    const headers = ["Zeitpunkt", "Benutzer-E-Mail", "Benutzer-ID", "Aktion", "Resource-Typ", "Resource-ID", "Details", "IP"];
    const rows = filtered.map((e) => [
      e.created_at || "",
      e.user_email || "",
      e.user_id || "",
      e.action || "",
      e.resource_type || "",
      e.resource_id || "",
      e.details ? JSON.stringify(e.details) : "",
      e.ip || "",
    ].map(csvEscape).join(","));
    const csv = "\uFEFF" + headers.map(csvEscape).join(",") + "\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `vmbs-audit-log_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-audit-tab">
      <AuditStatsCards />

      {/* Retention config */}
      <div data-testid="audit-retention-config" className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 flex-shrink-0">
          <Archive className="w-4 h-4 text-[#C5993A]" />
          <span className="text-sm text-white">Aufbewahrungsdauer</span>
        </div>
        <div className="flex items-center gap-2 flex-1">
          <Input
            data-testid="retention-input"
            type="number"
            min={7}
            max={3650}
            value={retentionInput}
            onChange={(e) => setRetentionInput(e.target.value)}
            className="bg-[#0C1620] border-[#3A4F60]/50 text-white h-9 w-28"
          />
          <span className="text-xs text-[#8899AA]">Tage (7–3650)</span>
        </div>
        <Button
          data-testid="save-retention"
          onClick={saveRetention}
          disabled={savingRetention || retentionInput === String(retention) || !retentionInput}
          size="sm"
          className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 h-9"
        >
          {savingRetention ? "Speichere..." : "Speichern"}
        </Button>
        <span className="text-[10px] text-[#3A4F60] sm:ml-2">Cleanup: täglich · Aktuell: <span className="text-[#8899AA] font-mono">{retention} Tage</span></span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-heading text-xl text-white flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-[#C5993A]" /> Audit-Log
          </h3>
          <p className="text-xs text-[#3A4F60] mt-1">Nachvollziehbarkeit aller Admin-Aktionen (neueste zuerst, letzte 300)</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="audit-action-filter"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="bg-[#12202f] border border-[#3A4F60]/50 text-white text-sm rounded-md h-9 px-3 focus:border-[#C5993A] focus:outline-none"
          >
            <option value="">Alle Aktionen ({entries.length})</option>
            {uniqueActions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <div className="relative">
            <Filter className="w-3.5 h-3.5 text-[#3A4F60] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="audit-filter-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter (email, …)"
              className="bg-[#12202f] border-[#3A4F60]/50 text-white text-sm pl-8 h-9 w-44 sm:w-56"
            />
          </div>
          <Button
            data-testid="audit-export-csv"
            onClick={exportCsv}
            disabled={filtered.length === 0}
            variant="ghost"
            size="sm"
            className="text-[#C5993A] hover:text-white hover:bg-[#C5993A] h-9 border border-[#C5993A]/40"
          >
            <Download className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">CSV ({filtered.length})</span>
          </Button>
          <Button
            data-testid="audit-refresh-button"
            onClick={fetchLog}
            variant="ghost"
            size="sm"
            className="text-[#8899AA] hover:text-[#C5993A] h-9"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg">
          <p className="text-[#8899AA]">{entries.length === 0 ? "Noch keine Einträge." : "Keine Treffer für aktuelle Filter."}</p>
        </div>
      ) : (
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-[#0C1620] text-[#8899AA] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Zeitpunkt</th>
                  <th className="text-left px-4 py-2.5 font-medium">Benutzer</th>
                  <th className="text-left px-4 py-2.5 font-medium">Aktion</th>
                  <th className="text-left px-4 py-2.5 font-medium">Resource</th>
                  <th className="text-left px-4 py-2.5 font-medium">Details</th>
                  <th className="text-left px-4 py-2.5 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} className="border-t border-[#3A4F60]/20 hover:bg-[#0C1620]/50" data-testid={`audit-row-${e.id}`}>
                    <td className="px-4 py-2.5 text-[#8899AA] whitespace-nowrap text-xs">{fmtDate(e.created_at)}</td>
                    <td className="px-4 py-2.5 text-white text-xs truncate max-w-[180px]">{e.user_email || "—"}</td>
                    <td className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap ${ACTION_COLORS[e.action] || "text-[#C5993A]"}`}>
                      {e.action}
                    </td>
                    <td className="px-4 py-2.5 text-[#8899AA] text-xs">
                      {e.resource_type ? (
                        <>
                          <span>{e.resource_type}</span>
                          {e.resource_id && (
                            <span className="block text-[10px] text-[#3A4F60] font-mono truncate max-w-[120px]">{e.resource_id}</span>
                          )}
                        </>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-[#8899AA] text-xs max-w-[280px]">
                      {e.details ? (
                        <code className="text-[10px] text-[#C5993A]/70 break-all line-clamp-2">
                          {JSON.stringify(e.details)}
                        </code>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-[#3A4F60] text-[10px] font-mono whitespace-nowrap">{e.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
